export type SchoolCalendarRule = CalendarRule & {
  recurrence: Recurrence & {
    days: Weekday[];
  };
};

export interface CalendarRule extends RuleDefinition {
  id?: string;
  calendarId?: string;
  priority?: number;
  version?: number;
}

export interface NonSchoolRule extends CalendarRule {
  startDate: number;
  type: RuleType.HOLIDAY | RuleType.OTHER;
}

export interface RuleDefinition {
  name: string;
  type: RuleType;
  startDate?: number;
  startTime: number;
  endDate?: number;
  endTime: number;
  groups?: string[];
  excludeGroups?: string[];
  recurrence?: Recurrence;
}

export enum RuleType {
  SCHOOL_TIME = "school_time",
  HOLIDAY = "holiday",
  OTHER = "other",
}

export interface Recurrence {
  frequency: Frequency;
  interval: number;
  until?: number;
  days?: Weekday[];
  exceptions?: RecurrenceException[];
}

export enum Frequency {
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
  YEARLY = "yearly",
}

export enum Weekday {
  MONDAY = "mon",
  TUESDAY = "tue",
  WEDNESDAY = "wed",
  THURSDAY = "thu",
  FRIDAY = "fri",
  SATURDAY = "sat",
  SUNDAY = "sun",
}

export const WeekdayFullNames: Record<Weekday, string> = {
  [Weekday.MONDAY]: "monday",
  [Weekday.TUESDAY]: "tuesday",
  [Weekday.WEDNESDAY]: "wednesday",
  [Weekday.THURSDAY]: "thursday",
  [Weekday.FRIDAY]: "friday",
  [Weekday.SATURDAY]: "saturday",
  [Weekday.SUNDAY]: "sunday",
};

// Stable weekday order for UI/logic.
// Object.values(Weekday) enum key iteration order isn’t guaranteed.
export const WEEKDAYS = [
  Weekday.MONDAY,
  Weekday.TUESDAY,
  Weekday.WEDNESDAY,
  Weekday.THURSDAY,
  Weekday.FRIDAY,
  Weekday.SATURDAY,
  Weekday.SUNDAY,
] as const;

export interface RecurrenceException {
  recurrenceId: RecurrenceID;
  type: RecurrenceExceptionType;
}

export type RecurrenceID = number;

export enum RecurrenceExceptionType {
  SKIPPED = "skipped",
}

export const SchoolTimePlaceholder: Readonly<SchoolCalendarRule> = Object.freeze({
  priority: 1,
  name: "School Hours",
  type: RuleType.SCHOOL_TIME,
  startTime: 830,
  endTime: 1530,
  recurrence: Object.freeze({
    frequency: Frequency.WEEKLY,
    interval: 1,
    days: [],
  }),
});

export const CalendarRuleIsNonSchoolRule = (r: CalendarRule): r is NonSchoolRule => {
  return r.type !== RuleType.SCHOOL_TIME && !!r.startDate;
};

export const CloneCalendarRule = <T extends CalendarRule>(r: T): T => {
  const clone = { ...r };
  if (r.recurrence) r.recurrence = { ...r.recurrence };
  return clone;
};
